/**
 * TextField and TextArea: labelled inputs with hint, error and a visible focus ring.
 *
 *   <TextField label="Name" value={name} onChange={e => setName(e.target.value)} />
 *   <TextField label="URL" type="url" leftIcon="mdi:link-variant" placeholder="https://example.com" required />
 *   <TextField label="Key name" error={errors.name} hint="Only you can see this." />
 *   <TextField aria-label="Search logs" leftIcon="mdi:magnify" size="sm" />          no visible label
 *   <TextField label="Password" type="password" />                                   adds a show/hide toggle
 *   <TextArea label="Message" rows={4} maxLength={500} showCount />
 *
 * label is linked with htmlFor; hint and error are linked with aria-describedby; error sets aria-invalid.
 * size sm 32px | md 36px (default) | lg 40px. Inputs render at 16px on phones so iOS does not zoom.
 * rightSlot renders inside the field on the right (a unit, a clear button). Every other prop goes to the <input>.
 */
import { forwardRef, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes, useId, useState } from "react";
import { Icon } from "@iconify/react";

type Size = "sm" | "md" | "lg";
const HEIGHTS: Record<Size, string> = { sm: "h-8", md: "h-9", lg: "h-10" };

export const fieldClasses = (invalid?: boolean) =>
  [
    "w-full rounded-control border bg-surface text-fg placeholder:text-fg-muted dark:bg-white/5",
    "transition-[border-color,box-shadow] duration-150 outline-none",
    "focus:border-primary focus:ring-3 focus:ring-primary/20",
    "disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-fg-muted",
    "read-only:bg-surface-muted",
    invalid ? "border-danger focus:border-danger focus:ring-danger/20" : "border-line-strong hover:border-fg-muted/60",
  ].join(" ");

function FieldShell({
  id,
  label,
  hint,
  error,
  required,
  className,
  children,
  hintId,
  errorId,
  trailingLabel,
}: {
  id: string;
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactNode;
  hintId: string;
  errorId: string;
  trailingLabel?: ReactNode;
}) {
  return (
    <div className={`min-w-0 ${className ?? ""}`}>
      {label && (
        <div className="mb-1.5 flex items-baseline justify-between gap-2">
          <label htmlFor={id} className="text-sm font-medium text-fg-secondary">
            {label}
            {required && (
              <span className="ml-0.5 text-danger-text" aria-hidden="true">
                *
              </span>
            )}
          </label>
          {trailingLabel}
        </div>
      )}
      {children}
      {error ? (
        <p id={errorId} className="mt-1.5 flex items-start gap-1 text-13 text-danger-text" role="alert">
          <Icon icon="mdi:alert-circle-outline" className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={hintId} className="mt-1.5 text-13 text-fg-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  size?: Size;
  leftIcon?: string | ReactNode;
  rightSlot?: ReactNode;
  /** Class for the outer wrapper (label + field + message). className goes to the <input>. */
  wrapperClassName?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, size = "md", leftIcon, rightSlot, wrapperClassName, className = "", id, type = "text", required, ...rest },
  ref
) {
  const autoId = useId();
  const inputId = id ?? `tf-${autoId}`;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const [reveal, setReveal] = useState(false);
  const isPassword = type === "password";
  const describedBy = [error ? errorId : hint ? hintId : null, rest["aria-describedby"]].filter(Boolean).join(" ") || undefined;

  return (
    <FieldShell
      id={inputId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={wrapperClassName}
      hintId={hintId}
      errorId={errorId}
    >
      <div className="relative">
        {leftIcon && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-fg-muted">
            {typeof leftIcon === "string" ? <Icon icon={leftIcon} aria-hidden="true" /> : leftIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          type={isPassword && reveal ? "text" : type}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={`${fieldClasses(!!error)} ${HEIGHTS[size]} px-3 text-sm ${leftIcon ? "pl-9" : ""} ${rightSlot || isPassword ? "pr-10" : ""} ${className}`}
          {...rest}
        />
        {isPassword ? (
          <button
            type="button"
            onClick={() => setReveal(v => !v)}
            aria-label={reveal ? "Hide password" : "Show password"}
            aria-pressed={reveal}
            className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:text-fg cursor-pointer"
          >
            <Icon icon={reveal ? "mdi:eye-off-outline" : "mdi:eye-outline"} className="text-lg" aria-hidden="true" />
          </button>
        ) : (
          rightSlot && <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center">{rightSlot}</div>
        )}
      </div>
    </FieldShell>
  );
});

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Show "12/500" next to the label when maxLength is set. */
  showCount?: boolean;
  wrapperClassName?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, hint, error, showCount, wrapperClassName, className = "", id, required, rows = 4, maxLength, value, ...rest },
  ref
) {
  const autoId = useId();
  const inputId = id ?? `ta-${autoId}`;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const count = typeof value === "string" ? value.length : 0;
  return (
    <FieldShell
      id={inputId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={wrapperClassName}
      hintId={hintId}
      errorId={errorId}
      trailingLabel={
        showCount && maxLength ? (
          <span className="text-xs tabular-nums text-fg-muted" aria-live="polite">
            {count}/{maxLength}
          </span>
        ) : undefined
      }
    >
      <textarea
        ref={ref}
        id={inputId}
        rows={rows}
        required={required}
        maxLength={maxLength}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : hint ? hintId : undefined}
        className={`${fieldClasses(!!error)} block px-3 py-2 text-sm leading-relaxed resize-y ${className}`}
        {...rest}
      />
    </FieldShell>
  );
});

export default TextField;
